import { SearchFilters } from '../types';
import { getDomainConfig, DEFAULT_LOCATION_OPTIONS } from '../config/appConfig';

export const WESTERN_EUROPE_COUNTRIES = [
  'Austria',
  'Belgium',
  'France',
  'Germany',
  'Liechtenstein',
  'Luxembourg',
  'Monaco',
  'Netherlands',
  'Switzerland',
  'Portugal',
  'Spain',
  'Italy',
  'Ireland',
  'Sweden',
  'Norway',
  'Denmark',
  'Finland',
  'Poland',
];

const GROUP_LABELS = new Set(['Western Europe', 'Rest of World']);

/** Full "Rest of World" country list before any per-tenant exclusions. */
export const REST_OF_WORLD_COUNTRIES = [
  // Americas
  'Mexico', 'Brazil', 'Argentina', 'Colombia', 'Chile', 'Peru', 'Venezuela', 'Ecuador', 'Bolivia',
  'Paraguay', 'Uruguay', 'Costa Rica', 'Panama', 'Guatemala', 'Cuba', 'Dominican Republic',
  'Honduras', 'El Salvador', 'Nicaragua', 'Jamaica', 'Trinidad and Tobago', 'Bahamas', 'Barbados',
  // Europe (non-Western Europe)
  'Romania', 'Hungary', 'Czech Republic',
  'Greece', 'Turkey', 'Russia', 'Ukraine', 'Bulgaria', 'Croatia', 'Serbia', 'Slovakia', 'Slovenia',
  'Lithuania', 'Latvia', 'Estonia', 'Iceland', 'Belarus', 'Moldova', 'North Macedonia', 'Albania',
  'Bosnia and Herzegovina', 'Montenegro', 'Kosovo', 'Cyprus', 'Malta',
  // Asia
  'India', 'Japan', 'South Korea', 'Philippines', 'Pakistan', 'Bangladesh', 'Indonesia',
  'Vietnam', 'Thailand', 'Malaysia', 'Singapore', 'Sri Lanka', 'Nepal', 'Myanmar', 'Cambodia',
  'Taiwan', 'Hong Kong', 'Kazakhstan', 'Uzbekistan', 'Azerbaijan', 'Georgia', 'Armenia',
  'Mongolia', 'Laos', 'Brunei', 'Macau',
  // Oceania
  'Australia', 'New Zealand', 'Fiji', 'Papua New Guinea', 'Samoa', 'Tonga',
  // Africa
  'Nigeria', 'South Africa', 'Egypt', 'Kenya', 'Ghana', 'Morocco', 'Algeria', 'Tunisia',
  'Ethiopia', 'Tanzania', 'Uganda', 'Senegal', 'Ivory Coast', 'Cameroon', 'Zimbabwe', 'Zambia',
  'Botswana', 'Rwanda', 'Mauritius', 'Libya', 'Sudan', 'Angola', 'Mozambique', 'Madagascar',
  // Middle East
  'Israel', 'United Arab Emirates', 'Saudi Arabia', 'Iran', 'Iraq', 'Jordan', 'Lebanon',
  'Qatar', 'Kuwait', 'Bahrain', 'Oman', 'Yemen', 'Syria', 'Palestine',
];

/**
 * Compute effective "Rest of World" countries by excluding any country
 * that appears as a standalone option in the tenant's locationOptions.
 */
function getRestOfWorldForDomain(domain: string): string[] {
  const opts = getDomainConfig(domain).locationOptions ?? DEFAULT_LOCATION_OPTIONS;
  const standaloneCountries = new Set(opts.filter((o) => !GROUP_LABELS.has(o)));
  return REST_OF_WORLD_COUNTRIES.filter((c) => !standaloneCountries.has(c));
}

/**
 * Build backend payload from filters (Western Europe / Rest of World expansion, empty-value stripping).
 * All criteria are hard (must-have); no criteriaType keys.
 */
export function buildSearchPayload(searchFilters: SearchFilters): SearchFilters {
  const cleanFilters: SearchFilters = { ...searchFilters };

  if (cleanFilters.countries && cleanFilters.countries.length > 0) {
    const domain = cleanFilters.domain ?? 'SWE';
    const expandedCountries: string[] = [];
    cleanFilters.countries.forEach((location) => {
      if (location === 'Western Europe') {
        expandedCountries.push(...WESTERN_EUROPE_COUNTRIES);
      } else if (location === 'Rest of World') {
        expandedCountries.push(...getRestOfWorldForDomain(domain));
      } else {
        expandedCountries.push(location);
      }
    });
    cleanFilters.countries = expandedCountries;
  }

  Object.keys(cleanFilters).forEach((key) => {
    const filterKey = key as keyof SearchFilters;
    const value = cleanFilters[filterKey];
    if (value === undefined || value === null || value === '') {
      delete cleanFilters[filterKey];
    } else if (Array.isArray(value) && value.length === 0) {
      delete cleanFilters[filterKey];
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      const obj = value as { min?: number; max?: number };
      if (obj.min === undefined && obj.max === undefined) {
        delete cleanFilters[filterKey];
      }
    }
  });

  const backendFilters: Record<string, unknown> = { ...cleanFilters };

  if (backendFilters.skills && Array.isArray(backendFilters.skills)) {
    const skills = (backendFilters.skills as { name?: string }[])
      .map((s) => (s?.name?.trim() ? { name: s.name.trim() } : null))
      .filter((s): s is { name: string } => s !== null);
    if (skills.length === 0) {
      delete backendFilters.skills;
    } else {
      backendFilters.skills = skills;
    }
  }
  backendFilters.pageSize = cleanFilters.pageSize ?? 18;
  return backendFilters as SearchFilters;
}
